import React from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {
  HelpCircle,
  MessageCircle,
  ClipboardList,
  Settings,
  LogOut,
} from 'lucide-react';

import {ThemeToggle} from './components/ui/theme-toggle';

import {useIsAdmin} from './state/hooks';
import {
  useSafeNavigate as useNavigate,
  SafeLink as Link,
} from './util/safeNavigate';
import {Grid} from './components/ui/grid';
import {Layout, Header, Content} from './components/ui/layout';
import {Image} from './components/ui/image';
import {Title} from './components/ui/typography';
import {Button} from './components/ui/button';
import {cn} from './lib/utils';
import nudgeLogo from './img/nudge.png';

import './App.css';

/**
 * Keys for the nav menu.
 */
const NAV = {
  Chat: {
    key: 'chat',
    url: '/chat',
  },
  Review: {
    key: 'review',
    url: '/review',
  },
  Settings: {
    key: 'settings',
    url: '/settings',
  },
  Help: {
    key: 'help',
    url: '/help',
  },
};

/**
 * Check which subpage of the site is active based on the URL.
 */
const getActiveNavKey = () => {
  const location = useLocation();
  const path = location.pathname;

  for (const {key, url} of Object.values(NAV)) {
    if (path.startsWith(url)) {
      return key;
    }
  }

  return '';
};

/**
 * Frame of the logged-in app (header + body).
 */
export const App = () => {
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const currentNavKey = getActiveNavKey();
  const isAdmin = useIsAdmin();

  return (
    <Layout>
      <Header
        id="nudge-header"
        className={cn(
          'flex items-center justify-between px-4 py-2 border-b bg-background',
          !screens.md && 'px-2',
        )}
      >
        <Link
          to="/"
          id="nudge-banner-logo"
          className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
        >
          <div className="w-6 h-6">
            <Image
              src={nudgeLogo}
              alt="Nudge Logo"
              className="w-full h-full object-contain"
            />
          </div>
          {screens.md && (
            <Title level={4} className="m-0">
              Nudge{' '}
              {isAdmin ? (
                <span className="text-muted-foreground font-normal">Admin</span>
              ) : null}
            </Title>
          )}
        </Link>

        <nav className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'flex items-center space-x-2',
              currentNavKey === NAV.Chat.key && 'bg-accent',
            )}
            onClick={() => navigate('/chat')}
          >
            <MessageCircle className="w-4 h-4 text-secondary" />
            <span className="hidden sm:inline">Chats</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'flex items-center space-x-2',
              currentNavKey === NAV.Review.key && 'bg-accent',
            )}
            onClick={() => navigate('/review')}
          >
            <ClipboardList className="w-4 h-4 text-secondary" />
            <span className="hidden sm:inline">Review</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'flex items-center space-x-2',
              currentNavKey === NAV.Settings.key && 'bg-accent',
            )}
            onClick={() => navigate(NAV.Settings.url)}
          >
            <Settings className="w-4 h-4 text-secondary" />
            <span className="hidden sm:inline">Settings</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'flex items-center space-x-2',
              currentNavKey === 'help' && 'bg-accent',
            )}
            onClick={() => navigate('/help')}
          >
            <HelpCircle className="w-4 h-4 text-secondary" />
            <span className="hidden sm:inline">Help</span>
          </Button>

          <ThemeToggle />

          <Button
            variant="ghost"
            size="sm"
            className="flex items-center space-x-2"
            onClick={() => navigate('/logout')}
          >
            <LogOut className="w-4 h-4 text-secondary" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </nav>
      </Header>

      <Content>
        <Outlet />
      </Content>
    </Layout>
  );
};
